import React, { FC } from "react";
import type { GridRow, GridColumn } from "@getregistered/greg-editors/dist/lib/domain/grid/types";
import { ActionMenu, ActionList, IconButton, ThemeProvider } from "@primer/react";
import { KebabHorizontalIcon, LocationIcon, PersonIcon } from "@primer/octicons-react";

interface EventRequestCardProps {
  row: GridRow;
  columns: GridColumn[];
}

interface ActionButton {
  href: string;
  text: string;
  key: string;
  type?: string;
  "data-confirm"?: string;
}

function getCellValue(row: GridRow, key: string): string {
  const val = row[key];
  if (val === null || val === undefined) return "--";
  if (typeof val === "object" && "displayValue" in val) return val.displayValue;
  return String(val);
}

function getHref(row: GridRow, key: string): string | null {
  const val = row[key];
  if (val && typeof val === "object" && "cellVariant" in val) {
    const variant = val.cellVariant;
    if (variant?.type === "drilldown" && variant.href) return variant.href;
  }
  return null;
}

// The status cell is a fill_label carrying EventRequest::STATUS_VARIANTS'
// colour, so the card tints from the same palette as the grid.
function getStatusInfo(row: GridRow): { label: string; variant: string } {
  const val = row["event_requests:status"] as Record<string, any> | null | undefined;
  if (val && typeof val === "object") {
    const label = val.displayValue || val.rawValue || "";
    const variant = val.cellVariant?.variant || "gray";
    return { label, variant };
  }
  return { label: "", variant: "gray" };
}

function getActionButtons(row: GridRow): ActionButton[] {
  const val = row["action_menu"] as Record<string, any> | null | undefined;
  if (val && Array.isArray(val.buttons)) return val.buttons as ActionButton[];
  return [];
}

// GR-1702: card renderer for the My Requests grid. Mirrors EventCard, but event
// requests have no banner image, so the header is always the status-tinted
// fallback (event-card-header--colour-<variant>).
export const EventRequestCard: FC<EventRequestCardProps> = ({ row }) => {
  const title = getCellValue(row, "event_requests:title");
  const href = getHref(row, "event_requests:title");
  const requestedDate = getCellValue(row, "event_requests:requested_date");
  const requester = getCellValue(row, "event_requests:requester");
  const facility = getCellValue(row, "event_requests:facility");
  const status = getStatusInfo(row);
  const actions = getActionButtons(row);

  const headerClass = ["event-card-header", `event-card-header--colour-${status.variant}`]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="event-card-inner test-event-request-card" data-event-request-id={row.id}>
      <div className={headerClass}>
        {status.label && (
          <span className={`event-card-status event-card-status--colour-${status.variant}`}>
            <span className="event-card-status-dot" aria-hidden="true" />
            <span className="event-card-status-label">{status.label}</span>
          </span>
        )}
        {actions.length > 0 && (
          // See EventCard: wrap in the app's own ThemeProvider so the ActionMenu
          // overlay's useTheme() resolves (greg-editors mounts a separate
          // @primer/react instance whose ThemeContext is otherwise empty).
          <ThemeProvider>
            <span className="event-card-menu">
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={KebabHorizontalIcon}
                    aria-label="Request Options"
                    variant="invisible"
                    size="small"
                    unsafeDisableTooltip
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList>
                    {actions.map((action) => (
                      <ActionList.LinkItem
                        key={action.key}
                        href={action.href}
                        {...(action.type === "delete"
                          ? { "data-turbo-method": "delete" }
                          : {})}
                        {...(action["data-confirm"] ? { "data-turbo-confirm": action["data-confirm"] } : {})}
                      >
                        {action.text}
                      </ActionList.LinkItem>
                    ))}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </span>
          </ThemeProvider>
        )}
      </div>
      <div className="event-card-body">
        <div className="event-card-body-main">
          <div className="event-card-title">
            {href ? (
              <a href={href} className="event-card-link">
                {title}
              </a>
            ) : (
              title
            )}
          </div>
          <ol>
            <li className="breadcrumb-item breadcrumb-item-meta breadcrumb-item-no-border text-semibold f6">
              {requestedDate}
            </li>
          </ol>
        </div>
      </div>
      <div className="event-card-footer">
        <div className="event-card-footer-item">
          <PersonIcon size={16} className="color-fg-muted" />
          <span className="f6 text-semibold color-fg-default">{requester}</span>
        </div>
        <div className="event-card-footer-item no-border">
          <LocationIcon size={16} className="color-fg-muted" />
          <span className="f6 text-semibold color-fg-default">{facility}</span>
        </div>
      </div>
    </div>
  );
};
